/**
 * taskLineExtractor.js
 *
 * Responsible for extracting specific pieces (completion mark,
 * priority, tags, attributes) from a single task line.
 *
 * Follows CLEAN principles by separating extraction from parsing
 * logic (see taskLineParser.js).
 */

import {
  validatePriority,
  REGEX_COMPLETION_MARK_FULL,
  REGEX_PRIORITY,
  REGEX_TAG
} from '@/domain/models/taskLine/taskLineHelpers';

/**
 * Parses a value from its textual representation into the
 * appropriate type (e.g., number, boolean, or string).
 *
 * @param {string} value - The value to parse.
 * @returns {any} - Parsed value (number, boolean, string).
 */
export const parseValue = (value) => {
  if (!isNaN(value)) {
    return Number(value);
  }
  if (/^["'].*["']$/.test(value)) {
    // Remove surrounding quotes
    return value.slice(1, -1);
  }
  if (value === 'true' || value === 'false') {
    return value === 'true';
  }
  return value;
};

/**
 * Extracts and removes a completion mark at the start of the line,
 * if present (one non-alphanumeric char + whitespace).
 *
 * Example of a match: "- ", "x ".
 *
 * @param {string} text - The line of text to examine.
 * @returns {{ completionMark: string|null, line: string }}
 */
export const extractCompletionMark = (text) => {
  const match = text.match(REGEX_COMPLETION_MARK_FULL);
  if (match) {
    const completionMark = match[1];
    const trimmedLine = text.slice(match[0].length).trim();
    return { completionMark, line: trimmedLine };
  }
  return { completionMark: null, line: text };
};

/**
 * Extracts and removes the priority token "!X" from the start
 * of the line, if present.
 *
 * X must be a valid priority char (0-9, A-Z, a-z).
 *
 * @param {string} text - The line of text to inspect.
 * @returns {{ priority: string|null, line: string }}
 */
export const extractPriority = (text) => {
  const match = text.match(REGEX_PRIORITY);
  const priority = match ? match[1] : null;
  let finalLine = text;
  if (validatePriority(priority)) {
    finalLine = text.replace(REGEX_PRIORITY, '').trim();
  }
  return { priority, line: finalLine };
};

/**
 * Extracts the raw attributes block (the last occurrence between '{' and '}')
 * from the line, if present, using regex.
 *
 * @param {string} text - The line containing potential attributes.
 * @returns {{ attributesBlock: string|null, line: string }}
 */
export const extractAttributesBlockString = (text) => {
  // Regex to match the last { ... } block in the string
  const regex = /\{([^{}]*)\}(?!.*\{)/;
  const match = text.match(regex);

  if (match) {
    const attributesBlock = match[1].trim();
    // Remove the matched { ... } from the original text
    const line = (
      text.slice(0, match.index).trim() +
      ' ' +
      text.slice(match.index + match[0].length).trim()
    ).trim();
    return { attributesBlock, line };
  }

  return { attributesBlock: null, line: text };
};

/**
 * Extracts and parses the attributes block "{...}" from the line,
 * if present. Returns an object with the parsed attributes and
 * the remaining line.
 *
 * @param {string} text - The full line of text.
 * @returns {{ attributes: Object, line: string }}
 */
export const extractAttributesBlock = (text) => {
  const { attributesBlock, line } = extractAttributesBlockString(text);
  if (attributesBlock) {
    const attrs = parseAttributes(attributesBlock);
    return { attributes: attrs, line };
  }
  return { attributes: {}, line };
};

/**
 * Extracts all tags "#something" from the line and returns them
 * along with the cleaned line.
 *
 * @param {string} text - The full line of text.
 * @returns {{ tags: string[], line: string }}
 */
export const extractTags = (text) => {
  const tagMatches = text.match(REGEX_TAG) || [];
  const uniqueTags = [...new Set(tagMatches.map((t) => t.trim()))];
  const updatedLine = text.replace(REGEX_TAG, '').trim();
  return { tags: uniqueTags, line: updatedLine };
};

/**
 * Extracts a specific attribute by key from the attributes block.
 * Removes the matched attribute from the block and rebuilds the
 * line accordingly.
 *
 * @param {string} line - The original line with possible attributes.
 * @param {string} attributeKey - The name of the attribute to extract.
 * @returns {{ attribute: { [key: string]: any } | null, line: string }}
 */
export const extractAttribute = (line, attributeKey) => {
  if (typeof line !== 'string' || !attributeKey) {
    throw new Error(
      'Invalid arguments. "line" and "attributeKey" must be provided.'
    );
  }
  const { attributesBlock, line: updatedLine } =
    extractAttributesBlockString(line);

  if (!attributesBlock) {
    return { attribute: null, line };
  }

  // Build a regex to find the key: ...
  const regex = new RegExp(`\\b${attributeKey}\\s*:\\s*([^,}]+)`, 'i');
  const match = attributesBlock.match(regex);
  if (!match) {
    return { attribute: null, line };
  }

  // Parse the extracted value
  const rawValue = match[1].trim();
  const parsedValue = parseValue(rawValue);

  // Remove this attribute from the block
  let cleanedBlock = attributesBlock.replace(regex, '');
  // Normalize separators after deletion so we never emit `{ , }` or a block
  // with a leading/trailing comma. This matters when a UI clears one attribute
  // (e.g. `rrule`) and expects the remaining block to stay canonical.
  cleanedBlock = cleanedBlock
    .replace(/,\s*,/g, ',')
    .replace(/^\s*,\s*/g, '')
    .replace(/\s*,\s*$/g, '')
    .trim();

  // Rebuild
  const finalLine = cleanedBlock
    ? `${updatedLine} { ${cleanedBlock.replace(/,\s*$/, '')} }`.trim()
    : updatedLine;

  return { attribute: { [attributeKey]: parsedValue }, line: finalLine };
};

/**
 * Splits text by a delimiter only when that delimiter is outside quoted
 * sections. This allows values like "a, b" to remain one attribute value.
 *
 * @param {string} text
 * @param {string} delimiter
 * @returns {string[]}
 */
const splitTopLevel = (text, delimiter) => {
  const parts = [];
  let current = '';
  let quoteChar = null;
  let escaped = false;

  for (let i = 0; i < text.length; i += 1) {
    const ch = text[i];

    if (quoteChar) {
      current += ch;
      if (escaped) {
        escaped = false;
        continue;
      }
      if (ch === '\\') {
        // Treat the next character as literal while inside quotes.
        escaped = true;
        continue;
      }
      if (ch === quoteChar) {
        quoteChar = null;
      }
      continue;
    }

    if (ch === '"' || ch === "'") {
      quoteChar = ch;
      current += ch;
      continue;
    }

    if (ch === delimiter) {
      parts.push(current.trim());
      current = '';
      continue;
    }

    current += ch;
  }

  parts.push(current.trim());
  return parts.filter((part) => part);
};

/**
 * Finds the first colon that is outside quotes in one attribute pair.
 *
 * @param {string} pair
 * @returns {number}
 */
const findTopLevelColonIndex = (pair) => {
  let quoteChar = null;
  let escaped = false;

  for (let i = 0; i < pair.length; i += 1) {
    const ch = pair[i];

    if (quoteChar) {
      if (escaped) {
        escaped = false;
        continue;
      }
      if (ch === '\\') {
        // Escape tracking mirrors splitTopLevel so both scanners agree.
        escaped = true;
        continue;
      }
      if (ch === quoteChar) {
        quoteChar = null;
      }
      continue;
    }

    if (ch === '"' || ch === "'") {
      quoteChar = ch;
      continue;
    }

    if (ch === ':') {
      return i;
    }
  }

  return -1;
};

/**
 * Parses all attributes from a block string of the form
 * key: value, key2: value2, ...
 *
 * This function is used internally by extractAttributesBlock
 * and is re-exported for direct usage in the parser module.
 *
 * @param {string} block - The raw attributes block (no braces).
 * @returns {Object} - Key-value map of the parsed attributes.
 */
export const parseAttributes = (block) => {
  if (!block) return {};

  const attributes = {};
  const parts = splitTopLevel(block, ',');

  parts.forEach((part) => {
    const colonIndex = findTopLevelColonIndex(part);
    // Fault tolerance: malformed entries are ignored without dropping peers.
    if (colonIndex === -1) return;

    const key = part.slice(0, colonIndex).trim();
    let value = part.slice(colonIndex + 1).trim();
    if (!key || !value) return;
    value = parseValue(value);
    attributes[key] = value;
  });

  return attributes;
};
